import type { RequestAndDetectLocationResult } from '../../shared/location/detectedLocationTypes';
import type { PrefillLocation } from '../LocationInputComponentCore';

import {
    extractCoordinates,
    extractStreetAddress,
    extractCity,
    extractAdmin1,
    extractPostalCode,
} from './locationDetectionHelpers';

/**
 * LocationDataInput type for GraphQL validation
 * This will be generated by GraphQL codegen, but defined here for reference
 */
export interface LocationDataInput {
    country: string;
    admin1?: string;
    admin2?: string;
    admin3?: string;
    admin4?: string;
    admin5?: string;
    admin6?: string;
    postalCode?: string;
    preciseLocation?: string;
}

/**
 * Converts a PrefillLocation to LocationDataInput for GraphQL validation
 *
 * @param prefill - The PrefillLocation to convert
 * @param country - The country code for the location
 * @returns LocationDataInput ready for GraphQL query
 */
export function prefillLocationToLocationDataInput(
    prefill: PrefillLocation,
    country: string,
): LocationDataInput {
    return {
        country: country,
        admin1: prefill.admin1,
        admin5: prefill.city,
        postalCode: prefill.postalCode,
        preciseLocation: prefill.streetAddress,
        // admin2, admin3, admin4, admin6 are not populated from PrefillLocation
        // These are typically derived by the backend during validation
    };
}

/**
 * Checks if a PrefillLocation has enough data to be validated
 *
 * @param prefill - The PrefillLocation to check
 * @returns true if the prefill has at least one location field populated
 */
export function isPrefillLocationValid(prefill: PrefillLocation): boolean {
    return !!(prefill.city || prefill.admin1 || prefill.postalCode || prefill.streetAddress);
}

/**
 * Profile location data from useLoadAndSaveLocation
 */
export interface ProfileLocationData {
    city?: string;
    stateAbbrev?: string;
    postalCode?: string;
    streetAddress?: string;
    latitude?: number;
    longitude?: number;
}

/**
 * Constructs a PrefillLocation from profile data
 *
 * @param profileData - Location data from useLoadAndSaveLocation hook
 * @returns PrefillLocation or undefined if no valid data
 */
export function profileDataToPrefillLocation(
    profileData: ProfileLocationData,
): PrefillLocation | undefined {
    // Check if we have at least one field
    if (
        !profileData.city &&
        !profileData.stateAbbrev &&
        !profileData.postalCode &&
        !profileData.streetAddress &&
        profileData.latitude === undefined &&
        profileData.longitude === undefined
    ) {
        return undefined;
    }

    return {
        city: profileData.city,
        admin1: profileData.stateAbbrev,
        postalCode: profileData.postalCode,
        streetAddress: profileData.streetAddress,
        latitude: profileData.latitude,
        longitude: profileData.longitude,
    };
}

/**
 * Constructs a PrefillLocation from RequestAndDetectLocationResult
 * Uses helper functions to extract data from the detectedLocation field
 *
 * @param result - The result from requestAndDetectLocation
 * @returns PrefillLocation or undefined if no valid data
 */
export function detectResultToPrefillLocation(
    result: RequestAndDetectLocationResult,
): PrefillLocation | undefined {
    const { detectedLocation } = result;

    if (!detectedLocation) {
        return undefined;
    }

    // Use helper functions from locationDetectionHelpers.ts
    const coordinates = extractCoordinates(detectedLocation);
    const streetAddress = extractStreetAddress(detectedLocation);
    const city = extractCity(detectedLocation);
    const admin1 = extractAdmin1(detectedLocation);
    const postalCode = extractPostalCode(detectedLocation);

    if (!coordinates && !streetAddress && !city && !admin1 && !postalCode) {
        return undefined;
    }

    return {
        latitude: coordinates?.latitude,
        longitude: coordinates?.longitude,
        streetAddress: streetAddress || undefined,
        city: city || undefined,
        admin1: admin1 || undefined,
        postalCode: postalCode || undefined,
    };
}
